import Keycloak from "keycloak-js";

// Cấu hình Keycloak v26 compatible
const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL , // URL Keycloak server
  realm: process.env.REACT_APP_KEYCLOAK_REALM,
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
};

// Khởi tạo Keycloak instance với config v26
const keycloak = new Keycloak(keycloakConfig);

// Cấu hình init options cho v26
export const initOptions = {
  onLoad: 'check-sso',
  silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
  checkLoginIframe: false, // Tắt để tránh lỗi iframe
  timeSkewTolerance: 30, // Cho phép chênh lệch thời gian 30 giây
  responseMode: 'fragment',
  flow: 'standard',
  pkceMethod: 'S256' // PKCE cho bảo mật tốt hơn
};

/**
 * KeycloakManager - Singleton class để quản lý Keycloak instance
 * Đảm bảo chỉ có một instance duy nhất trong toàn bộ ứng dụng
 */
class KeycloakManager {
  constructor() {
    // Kiểm tra nếu instance đã tồn tại
    if (KeycloakManager.instance) {
      return KeycloakManager.instance;
    }

    // Khởi tạo các thuộc tính
    this.keycloakInstance = null;
    this.isInitialized = false;
    this.isInitializing = false;
    this.initPromise = null;
    this.config = keycloakConfig;
    this.options = initOptions;

    // Lưu reference của instance
    KeycloakManager.instance = this;
  }

  /**
   * Lấy instance của KeycloakManager (Singleton pattern)
   * @returns {KeycloakManager} - Instance duy nhất
   */
  static getInstance() {
    if (!KeycloakManager.instance) {
      KeycloakManager.instance = new KeycloakManager();
    }
    return KeycloakManager.instance;
  }

  /**
   * Khởi tạo Keycloak với cấu hình mặc định hoặc custom
   * @param {Object} customConfig - Cấu hình tùy chỉnh (optional)
   * @param {Object} customOptions - Options tùy chỉnh (optional)
   * @returns {Promise<boolean>} - Promise trả về trạng thái authenticated
   */
  async initialize(callback = () => {}, customConfig = null, customOptions = null) {
    // Nếu đang trong quá trình khởi tạo, trả về promise hiện tại
    if (this.isInitializing && this.initPromise) {
      return this.initPromise;
    }

    // Nếu đã khởi tạo rồi, trả về trạng thái hiện tại
    if (this.isInitialized && this.keycloakInstance) {
      return this.keycloakInstance.authenticated || false;
    }

    // Bắt đầu quá trình khởi tạo
    this.isInitializing = true;
    
    this.initPromise = this._performInitialization(customConfig, customOptions, callback);
    
    try {
      const result = await this.initPromise;
      this.isInitialized = true;
      this.isInitializing = false;
      return result;
    } catch (error) {
      this.isInitializing = false;
      this.initPromise = null;
      throw error;
    }
  }

  /**
   * Thực hiện khởi tạo Keycloak
   * @private
   */
  async _performInitialization(customConfig, customOptions, callback) {
    try {
      // Sử dụng config tùy chỉnh hoặc mặc định
      const configToUse = customConfig || this.config;
      const optionsToUse = customOptions || this.options;

      // Tạo instance Keycloak mới nếu chưa có hoặc config thay đổi
      if (!this.keycloakInstance || customConfig) {
        this.keycloakInstance = new Keycloak(configToUse);
      }

      // Khởi tạo Keycloak
      const authenticated = await this.keycloakInstance.init(optionsToUse);
      
      if (authenticated && this.keycloakInstance.token) {
        callback(this.keycloakInstance.token);
        // Setup token refresh
        this._setupTokenRefresh();
      }

      return authenticated;
    } catch (error) {
      console.error("❌ KeycloakManager init error:", error);
      throw error;
    }
  }

  /**
   * Thiết lập auto refresh token
   * @private
   */
  _setupTokenRefresh() {
    if (!this.keycloakInstance) return;

    // Refresh token mỗi 50 giây nếu token sắp hết hạn
    setInterval(() => {
      this.keycloakInstance.updateToken(70).then((refreshed) => {
        if (refreshed) {
          console.log('🔄 Token refreshed');
        }
      }).catch((error) => {
        console.error('❌ Token refresh failed:', error);
      });
    }, 50000);
  }

  /**
   * Lấy Keycloak instance
   * @returns {Keycloak|null} - Keycloak instance hoặc null
   */
  getKeycloakInstance() {
    return this.keycloakInstance;
  }

  /**
   * Lấy token hiện tại
   * @returns {string|null} - JWT token hoặc null
   */
  getToken() {
    return this.keycloakInstance?.token || null;
  }

  /**
   * Kiểm tra trạng thái authenticated
   * @returns {boolean} - True nếu đã authenticated
   */
  isAuthenticated() {
    return this.keycloakInstance?.authenticated || false;
  }

  /**
   * Lấy thông tin user từ token
   * @returns {Object|null} - User info hoặc null
   */
  getUserInfo() {
    return this.keycloakInstance?.tokenParsed || null;
  }

  /**
   * Refresh token
   * @param {number} minValidity - Thời gian tối thiểu token còn hiệu lực (giây)
   * @returns {Promise<boolean>} - True nếu refresh thành công
   */
  async refreshToken(minValidity = 70) {
    if (!this.keycloakInstance) {
      throw new Error('Keycloak not initialized');
    }

    try {
      const refreshed = await this.keycloakInstance.updateToken(minValidity);
      if (refreshed) {
        console.log('🔄 Token refreshed successfully');
      }
      return refreshed;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Đăng nhập
   * @param {Object} options - Login options
   * @returns {Promise<void>}
   */
  async login(options = {}) {
    if (!this.keycloakInstance) {
      throw new Error('Keycloak not initialized');
    }
    
    return this.keycloakInstance.login(options);
  }

  /**
   * Đăng xuất
   * @param {Object} options - Logout options
   * @returns {Promise<void>}
   */
  async logout(options = {}) {
    if (!this.keycloakInstance) {
      throw new Error('Keycloak not initialized');
    }

    return this.keycloakInstance.logout(options);
  }

  /**
   * Reset instance (chỉ sử dụng cho testing)
   * @private
   */
  _reset() {
    this.keycloakInstance = null;
    this.isInitialized = false;
    this.isInitializing = false;
    this.initPromise = null;
    KeycloakManager.instance = null;
  }

  /**
   * Lấy trạng thái khởi tạo
   * @returns {Object} - Object chứa các trạng thái
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isInitializing: this.isInitializing,
      isAuthenticated: this.isAuthenticated(),
      hasInstance: !!this.keycloakInstance,
      token: this.getToken(),
      userInfo: this.getUserInfo()
    };
  }
}

// Export singleton instance
export const keycloakManager = KeycloakManager.getInstance();

